import { Backdrop, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Popover, TextareaAutosize, TextField } from "@mui/material";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import socket, { IResponse } from "../../socket";
import ChannelHeader from "./ChannelHeader";
import IChannel, { IMessage } from "./IChannel";
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import Message from "./Message";
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from "react-i18next";
import ImageIcon from '@mui/icons-material/Image';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import axios from "axios";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';



export default function Channel() {
    const { t } = useTranslation();
    const location = useLocation();
    const { id } = useParams();
    const [channel, setChannel] = useState<IChannel | undefined>(location.state?.channel);
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [type, setType] = useState("");
    const [txtLink, setTxtLink] = useState('');
    const [loadingLink, setLoadingLink] = useState(false);

    const handleOpenAttachment = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const fileImage = useRef<HTMLInputElement>(null);
    const txtMessage = useRef<HTMLTextAreaElement>(null);
    const txtDescription = useRef<HTMLInputElement>(null);
    const list = useRef<VirtuosoHandle>(null);


    useEffect(() => {
        if (!channel) {
            socket.emit('channel:info', id, (data: IResponse<IChannel>) => {
                if (data.success) {
                    setChannel(data.result);
                }
            })
        }
        loadMoreMessage();
        socket.on('channel:message', (data: { channelId: number, messasge: IMessage }) => {
            if (Number(id) === data.channelId) {
                setMessages([...messages, data.messasge]);
            }
        })
        return () => {
            socket.off('channel:message');
            socket.emit("network:leave");
        }
    }, []);

    const loadMoreMessage = () => {
        const messageRequest: { channelId: number, before?: Date } = {
            channelId: Number(id),
        }
        if (messages.length) {
            messageRequest.before = messages[0].createdAt;
        }
        setLoading(true);
        socket.emit("channel:messages", messageRequest, (data: IResponse<IMessage[]>) => {
            setLoading(false)
            if (data.success) {
                const newMessages = data.result.map(message => {
                    message.createdAt = new Date(message.createdAt);
                    return message
                })
                setMessages([...newMessages.reverse(), ...messages]);
            }
        })
    }

    const handleSend = (event: any) => {
        event.preventDefault();
        let data: any;
        if (txtMessage.current?.value.length) {
            data = {
                text: txtMessage.current!.value.replaceAll('\n', '\n\r'),
                channelId: id,
                type: "text",
            }
            txtMessage.current!.value = '';
        }
        if (txtLink.length) {
            if (type === "image") {
                data = {
                    image: txtLink,
                    text: txtDescription.current!.value.replaceAll('\n', '\n\r'),
                    channelId: id,
                    type,
                }
            } else if (type === "video") {
                data = {
                    video: txtLink,
                    text: txtDescription.current!.value.replaceAll('\n', '\n\r'),
                    channelId: id,
                    type,
                }
            }
            setTxtLink('');
            txtDescription.current!.value = '';
            setOpenDialog(false);
        }
        if (data) {
            setSendingMessage(true);
            socket.emit('channel:send_message', data, (data: IResponse<IMessage>) => {
                setSendingMessage(false);
                if (data.success) {
                    const message = data.result;
                    message.createdAt = new Date(message.createdAt);
                    setMessages([...messages, message]);
                    setTimeout(() => {
                        list.current!.scrollToIndex({
                            index: messages.length + 1,
                            align: 'end',
                            behavior: 'smooth',
                        });
                    }, 50);
                }
            });
        }
    }
    const handleKeyDown = (event: any) => {
        if (event.keyCode === 13 && !event.shiftKey) {
            handleSend(event);
        }
    }

    const handleOpenImageDialog = () => {
        handleOpenDialog('image');
    }

    const handleOpenVideoDialog = () => {
        handleOpenDialog('video');
    }

    const handleOpenDialog = (dialogType: 'image' | 'video') => {
        setType(dialogType);
        setOpenDialog(true);
        setAnchorEl(null);
    }

    const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length === 1) {
            setLoadingLink(true);
            const formData = new FormData();
            formData.append('file', files[0]);
            axios({
                url: `${process.env.REACT_APP_BASE_URL ?? 'http://localhost:5000/api/'}/upload`,
                method: 'POST',
                data: formData,
                headers: {
                    Authorization: localStorage.getItem('admin_rezvan_token')
                }
            }).then((res) => {
                setLoadingLink(false);
                if (res.data.status) {
                    setTxtLink(res.data.result);
                } else {
                    // TODO: show error message
                }
            }).catch((err) => {
                setLoadingLink(false);
            });

        }
    };

    const handleClickUploadFile = () => {
        fileImage.current!.click();
    }

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setTxtLink('');
    }

    const handleDeleteMessage = (id: number) => {
        setMessages(messages => messages.filter(message => message.id !== id))
    }


    return (
        <div className="flex flex-col h-full">
            <ChannelHeader channel={channel} />
            <Box className="grow px-4" sx={{ bgcolor: 'grey.50' }}>
                {loading && <div className="absolute z-10 left-1/2"><CircularProgress size={12} /></div>}
                {
                    messages.length ? <Virtuoso
                        ref={list}
                        data={messages}
                        initialTopMostItemIndex={500}
                        alignToBottom={true}
                        startReached={loadMoreMessage}
                        itemContent={(index, message) => <Message message={message} deleteMessage={handleDeleteMessage} />}
                    /> : <></>
                }
            </Box>
            <Divider />
            <Box className="flex flex-row items-end rtl:space-x-reverse space-x-1 p-2" >
                <IconButton onClick={handleOpenAttachment} color="primary">
                    <AttachFileIcon />
                </IconButton>
                <TextareaAutosize
                    maxRows={4}
                    onKeyDown={handleKeyDown}
                    ref={txtMessage}
                    className="grow outline-none resize-none min-h-[32px]"
                    placeholder={t('message_text_box_placeholder')} />
                {
                    sendingMessage ?
                        <CircularProgress />
                        :
                        <IconButton color="primary" onClick={handleSend}>
                            <SendIcon className="rtl:-scale-x-100" />
                        </IconButton>
                }
            </Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <Box className="flex flex-col items-center p-1">
                    <Button onClick={handleOpenImageDialog} startIcon={<ImageIcon />}>{t('image')}</Button>
                    <Button onClick={handleOpenVideoDialog} startIcon={<OndemandVideoIcon />}>{t('video')}</Button>
                </Box>
            </Popover>
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{t('send_file')}</DialogTitle>
                <DialogContent>
                    <div className="flex flex-row space-x-2 space-x-reverse items-center">
                        <TextField value={txtLink} margin="dense" label={t('link')} className='grow' disabled />
                        <input type='file' className='hidden' ref={fileImage} onChange={handleChangeFile} />
                        <IconButton color='primary' onClick={handleClickUploadFile}>
                            <CloudUploadIcon />
                        </IconButton>
                    </div>
                    <TextField
                        multiline
                        maxRows={4}
                        margin="dense"
                        label={t('description')}
                        fullWidth
                        inputRef={txtDescription}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{t('close')}</Button>
                    <Button variant="contained" onClick={handleSend}>{t('send')}</Button>
                </DialogActions>
            </Dialog>
            <Backdrop
                sx={{ color: 'primary.main', zIndex: 10000}}
                open={loadingLink}
                onClick={handleClose}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    )
}
